import React from "react";
import DetailedView from "@/components/layouts/DetailedView";
import { useParams } from "react-router-dom";
import { useCompanies } from "@/pages/companies/hooks";
import CompanyTopFields from "@/pages/companies/components/CompanyTopFields";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { ComputedFields } from "@/services/api/types";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import { useCompanyActions } from "@/services/hooks/entities/company/useCompanyActions";
import { Button } from "@/components/ui/button";
import { CompanyStatus } from "@/pages/companies/types";
import { useTranslation } from "react-i18next";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { UserData } from "@/entities/users/types";
import { EntityData } from "@/services/api/types";
import CompanyOverviewContent from "./CompanyOverviewContent";

interface Props {
  userData: UserData;
}

const CompanyDetailedView: React.FC<Props> = ({ userData }) => {
  const { companyId } = useParams<{ companyId: string }>();

  const { t } = useTranslation();

  const { isDropdownClosing } = useDropdownClosing();

  const {
    entities: [companyEntity],
    isError: isCompanyError
  } = useCompanies([companyId || ""], {
    forceRefetch: true,
    computed_fields: ComputedFields.ALL
  });

  const {
    company,
    isTasksDrawerOpen,
    toggleTasksDrawer,
    onUpdateField,
    getTopOptions,
    getCreateOptions,
    getOptions,
    renderModals
  } = useCompanyActions(companyEntity, true);

  return (
    <>
      <DetailedView
        entityData={company as EntityData}
        entityType={EntityType.COMPANY}
        isError={isCompanyError}
        options={getOptions()}
        topOptions={getTopOptions()}
        topRightComponent={
          <div
            data-test="company-detailed-view-actions"
            className="flex items-center gap-2"
          >
            {company?.status === CompanyStatus.ON_HOLD_FOR_PROSPECTING ? (
              <Button
                data-test="company-detailed-view-mark-prospect-button"
                variant="secondaryNoBg"
                onClick={() => onUpdateField("status", CompanyStatus.PROSPECT)}
              >
                {t("companies.companyView.markAsProspect")}
              </Button>
            ) : undefined}
            <OptionsDropdownMenu
              options={getCreateOptions()}
              tPrefix="detailedView.create"
              triggerIcon="add"
              iconSize="lg"
              triggerDataTest="company-detailed-view-create-entity-button"
            />
          </div>
        }
        onUpdateField={onUpdateField}
        isTasksDrawerOpen={isTasksDrawerOpen}
        toggleTasksDrawer={toggleTasksDrawer}
        topFields={
          <CompanyTopFields
            data-test="company-detailed-view-top-fields"
            company={company}
            onUpdateField={onUpdateField}
            className="min-h-[2.7rem]"
          />
        }
        overviewContent={
          <CompanyOverviewContent
            data-test="company-detailed-view-overview"
            company={company}
            onUpdateField={onUpdateField}
            getCreateOptions={getCreateOptions}
          />
        }
      />
      {renderModals(isDropdownClosing, false, undefined, userData)}
    </>
  );
};

export default CompanyDetailedView;
